import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateFormInputDirective } from '@/app/components/form/date-form-input.directive';
import { ComplaintService } from '@/app/core/services/complaint.service';

@Component({
  selector: 'app-complaint-channel',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateFormInputDirective],
  templateUrl: './complaint-channel.component.html',
  styleUrls: ['./complaint-channel.component.scss'],
})
export class ComplaintChannelComponent implements OnInit {
  complaintForm!: FormGroup;
  isSubmitting = false;
  successMsg: string | null = null;
  errorMsg: string | null = null;
  showFullPolicy = false;
  complaintFile: File | null = null;
  fileError: string | null = null;
  readonly maxFileSize = 5 * 1024 * 1024; // 5MB
  readonly allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'svg', 'docx'];

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private complaintService: ComplaintService,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  toggleFullPolicy(): void {
    this.showFullPolicy = !this.showFullPolicy;
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    this.fileError = null;

    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isValidExtension = extension && this.allowedExtensions.includes(extension);
      const isValidSize = file.size <= this.maxFileSize;

      if (!isValidExtension) {
        this.fileError =
          'Formato de archivo no permitido. Solo se aceptan: pdf, jpg, png, svg y docx.';
        this.complaintFile = null;
      } else if (!isValidSize) {
        this.fileError = `El archivo supera el peso máximo de ${this.maxFileSize / (1024 * 1024)} MB.`;
        this.complaintFile = null;
      } else {
        this.complaintFile = file;
      }
    } else {
      this.complaintFile = null;
    }
  }

  removeFile(): void {
    this.complaintFile = null;
    this.fileError = null;
  }

  private initForm(): void {
    this.complaintForm = this.fb.group({
      complaintType: ['', Validators.required],
      description: ['', [Validators.required, Validators.minLength(10)]],
      incidentDate: ['', Validators.required],
      department: [1],
      isAnonymous: [false],
      contactName: [''],
      contactEmail: ['', [Validators.email]],
      contactPhone: ['', [Validators.pattern(/^[0-9+-]*$/)]],
    });

    // Toggle contact validators based on anonymity
    this.complaintForm.get('isAnonymous')?.valueChanges.subscribe((isAnonymous) => {
      const contactFields = ['contactName', 'contactEmail', 'contactPhone'];
      contactFields.forEach((field) => {
        const control = this.complaintForm.get(field);
        if (isAnonymous) {
          control?.clearValidators();
        } else {
          if (field === 'contactEmail') {
            control?.setValidators([Validators.required, Validators.email]);
          } else if (field === 'contactPhone') {
            control?.setValidators([Validators.required, Validators.pattern(/^[0-9+-]*$/)]);
          } else {
            control?.setValidators([Validators.required]);
          }
        }
        control?.updateValueAndValidity();
      });
    });
  }

  onSubmit(): void {
    if (this.complaintForm.valid && !this.fileError) {
      this.isSubmitting = true;
      this.successMsg = null;
      this.errorMsg = null;

      const formValue = this.complaintForm.value;
      const payload = {
        ...formValue,
        complaintType: Number(formValue.complaintType),
        department: Number(formValue.department),
        complaintFile: this.complaintFile,
      };

      this.complaintService.createComplaint(payload).subscribe({
        next: (res) => {
          this.successMsg = 'Denuncia enviada satisfactoriamente';
          this.isSubmitting = false;
          this.complaintForm.reset({
            department: 1,
            isAnonymous: false,
            complaintType: '',
            description: '',
            incidentDate: '',
          });
          setTimeout(() => {
            this.activeModal.close(res);
          }, 3000);
        },
        error: (err) => {
          console.error('Error submitting complaint', err);
          this.errorMsg = 'No pudimos enviar tu denuncia. Intenta nuevamente.';
          this.isSubmitting = false;
        },
      });
    } else {
      this.markFormGroupTouched(this.complaintForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  onPhoneInput(event: any): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    input.value = value.replace(/[^0-9+-]/g, '');
    this.complaintForm.get('contactPhone')?.setValue(input.value, { emitEvent: false });
  }
}
